import { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import DataGrid from 'app/components/DataGrid';
import classes from 'app/components/DataGrid/classes.module.scss';
import useMomentsUploadForm from 'app/hooks/useMomentsUploadForm';
import UploadUploadProgressBar from './UploadUploadProgressBar';
import UploadCompletedCount from 'app/components/UploadCompletedCount';
import UploadWorker from 'app/workers/UploadWorker';
import MomentsUploadModal from './MomentsUploadModal';
import MomentsCell from 'app/components/DataGrid/MomentsCell';
import useDeeplink from 'app/hooks/useDeeplink';
import useUploads from 'app/hooks/useUploads';

import {
  setMomentParams
} from 'app/actions/moments';

import {
  getJobConfigs
} from 'app/services/jobConfigs';

import {
  setPhotoSwipeOpen,
  setPhotoSwipeMoment
} from 'app/actions/photoSwipeActions';

import {
  AND_OPERATOR,
} from 'app/hooks/useFilter';

import {
  moments
} from 'app/resources';

const Uploads = props => {
  // uploading from the Moments grid is only enabled for kiosk accounts
  const account = useSelector(state => state.portalReducer.account);
  const showMomentUpload = account.feature_flags.kiosk_mode && !account.feature_flags.disable_upload;

  const [jobConfigs, setJobConfigs] = useState([]);
  const loadJobConfigs = async () => {
    let resp = await getJobConfigs({ page: 1, per_page: 100 });
    setJobConfigs(resp.records);
  }
  useEffect(() => {
    loadJobConfigs();
  }, []);

  const dispatch = useDispatch();
  const deeplink = useDeeplink();

  // use useUploads for the grid (but not the form)
  const {
    resource,
    showFilters,
    onDataGridLoad,
    getRowData,
    refreshGrid,
    updateRow
  } = useUploads();

  // use useMomentsUploadForm for the form and add button.
  // normally useResource would automatically refresh the grid on submit,
  // but for useMomentsUploadForm this will need to be done manually
  const onUploadStart = () => {
    refreshGrid();
  }

  const onUploadComplete = upload => {
    updateRow(upload);
  }

  const {
    formIsOpen,
    onFormClose,
    onFormSubmit,
    onAdd
  } = useMomentsUploadForm({
    onUploadStart,
    onUploadComplete
  });

  // use uploadWorker to generate upload preview while uploading
  const uploadWorker = useContext(UploadWorker);

  const viewMomentsForUpload = async uploadId => {
    // filter moments by job ahead of showing the moments table.
    // this will overwrite any filters previously set.
    // currently the filter expects values to be strings
    let filterModel = {
      upload_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: uploadId.toString(),
        operator: AND_OPERATOR
      }]
    }

    deeplink.load(moments, filterModel);
  }

  const header = 'Uploads';
  const addButtonLabel = 'New Upload';
  const columnOrder = ['moments', 'status', 'file_count', 'completed_count', 'created_at', 'completed_at'];
  const visibleColumns = columnOrder;
  const omitColumns = ['completed', 'remaining'];

  const onMomentClick = momentId => {
    dispatch(setPhotoSwipeMoment(momentId));
    dispatch(setPhotoSwipeOpen(true));
  }

  const setPhotoSwipeItem = moment => ({
    id: moment.id,
    src: moment.preview_url,
    w: moment.width,
    h: moment.height
  });

  const columnDefOverrides = [
    {
      field: 'moments',
      headerName: 'Moments',
      pinned: 'left',
      inputType: 'number',
      width: 13 * 16,
      thumbnail: async data => {
        if (data) {
          if (data.cover_moment_uploaded) {
            return data.thumb_url;
          } else {
            return new Promise(resolve => {
	      // the upload record is the "group" for the moments
	      let groupId = data.id;
              uploadWorker.loadGroupPreview(groupId, resolve);
            });
          }
        }
        return null;
      },
      onThumbnailClick: data => onMomentClick(data),
      cellRenderer: params => {
	const {
          data,
	  colDef
	} = params;

        if (data) {
          const {
	    id,
	    file_count
	  } = data;

          const onImagesClick = () => viewMomentsForUpload(id);

	  return (
	    <MomentsCell
	      data={data}
	      colDef={colDef}
	      showImageCount={true}
	      imageCount={file_count}
	      onImagesClick={onImagesClick}
	    />
	  );
        }
        return '';
      }
    },
    { field: 'created_at', formatType: 'date' },
    { field: 'updated_at', formatType: 'date' },
    { field: 'completed_at', formatType: 'date' },
    {
      field: 'status',
      width: 220,
      cellRenderer: params => {
        let data = params.data;
        if (data) {
          if (data.completed_at) {
            return <span className={classes.gridCell}>Complete</span>;
          } else {
            return <UploadUploadProgressBar upload={data} />;
          }
        }
        return '';
      }
    },
    { field: 'file_count', width: 100 },
    {
      field: 'completed_count',
      headerName: 'Completed',
      width: 100,
      cellRenderer: params => {
        let data = params.data;
        if (data) {
          return <UploadCompletedCount upload={data} />;
        }
        return '';
      }
    }
  ]

  const callback = selectedRows => {
    console.log(selectedRows);
  }

  const actions = [
    { key: 'cancel', label: 'Cancel Uploads', callback },
    { key: 'destroy', label: 'Delete Uploads', callback }
  ]

  return (
    <div>
      <DataGrid
        resource={resource}
        header={header}
        getRowData={getRowData}
        addButtonLabel={addButtonLabel}
        onAdd={showMomentUpload && onAdd}
        columnOrder={columnOrder}
        visibleColumns={visibleColumns}
        omitColumns={omitColumns}
        columnDefOverrides={columnDefOverrides}
        setPhotoSwipeItem={setPhotoSwipeItem}
        showFilters={showFilters}
        loadingMessage="Loading uploads..."
        showSelect={false}
        //actions={actions}
        onDataGridLoad={onDataGridLoad}
      />
      <MomentsUploadModal
        isOpen={formIsOpen}
        onClose={onFormClose}
        onFormSubmit={onFormSubmit}
        jobConfigs={jobConfigs}
      />
    </div>
  );
}

export default Uploads;
